import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  Observable,
  Subject,
} from "rxjs";
import { TreeControlService } from "./tree-control.service";
import { TreeDataSourceService } from "./tree-data-source.service";
import { TreeListService } from "./tree-list.service";
import { TreeNode } from "./tree-node";
import { TreeScrollService } from "./tree-scroll.service";

@UntilDestroy()
@Component({
  selector: "red-virtual-tree",
  templateUrl: "./virtual-tree.component.html",
  styleUrls: ["./virtual-tree.component.scss"],
  providers: [
    TreeListService,
    TreeDataSourceService,
    TreeControlService,
    TreeScrollService,
  ],
})
export class VirtualTreeComponent implements OnInit, AfterViewInit {
  @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
  @Input() items: any[] = [];
  @Input() title: string;
  @Input() selected: Subject<any[]>;

  itemsFiltered = new BehaviorSubject<any>(null);
  itemsFiltered$: Observable<any> = this.itemsFiltered.asObservable();
  selectedItems = [];

  searchCtrl = new UntypedFormControl();
  searchPlaceholder = $localize`Search`;

  @ViewChild("scrollableTree") scrollableTree: CdkVirtualScrollViewport;

  constructor(
    public treeControl: TreeControlService,
    public dataSource: TreeDataSourceService,
    private scroll: TreeScrollService
  ) {
    dataSource.treeControl = treeControl;
  }

  nodesSelected: TreeNode[] = [];
  scrollOffset: number;
  isProcessing: boolean = false;
  backupSelection = [];

  ngOnInit() {
    this.dataSource.init(this.items);

    this.searchCtrl.valueChanges
      .pipe(untilDestroyed(this), debounceTime(150), distinctUntilChanged())
      .subscribe((value) => {
        this.onFilterChange(value);
      });
  }

  ngAfterViewInit(): void {
    this.scroll.init(this.scrollableTree);
  }

  // filter recursively on a text string using property object value
  filterRecursive(filterText: string, array: any[], property: string) {
    let filteredData;

    //make a copy of the data so we don't mutate the original
    function copy(o: any) {
      return Object.assign({}, o);
    }

    // has string
    if (filterText) {
      // need the string to match the property value
      filterText = filterText.toLowerCase();
      // copy obj so we don't mutate it and filter
      filteredData = array.map(copy).filter(function x(y) {
        if (y[property].toLowerCase().includes(filterText)) {
          return true;
        }
        // if children match
        if (y.children) {
          return (y.children = y.children.map(copy).filter(x)).length;
        }
      });
      // no string, return whole array
    } else {
      filteredData = array;
    }

    return filteredData;
  }

  // pass mat input string to recursive function and return data
  filterTree(filterText: string) {
    const filteredTree = this.filterRecursive(filterText, this.items, "item");

    // console.log("filteredTree", filteredTree);

    // use filter input text, return filtered TREE_DATA, use the 'name' object value
    this.dataSource.init(filteredTree);
  }

  // filter string from mat input filter
  onFilterChange(value: string) {
    this.filterTree(value);
    // show / hide based on state of filter string
    if (value) {
      this.treeControl.expandAll();
    } else {
      this.treeControl.collapseAll();
    }
  }
}
